<template>
  <div class="matching-rules space-y-6 animate-[fadeIn_0.6s_ease-out]">
    <!-- 页面标题 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F5D76E] to-[#F2C94C] flex items-center justify-center shadow-sm">
          <svg class="w-5 h-5 text-[#1B3C59]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-black text-slate-900">匹配规则</h3>
        </div>
      </div>
      <button
        v-if="hasPerm(PERMISSIONS.EDIT_CONFIG)"
        @click="handleSave"
        class="px-5 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
      >
        保存变更
      </button>
    </div>

    <!-- 前台关联提示 -->
    <div class="bg-blue-50/80 backdrop-blur-sm border border-blue-100/60 rounded-2xl p-4 flex items-start gap-3 text-sm text-blue-700">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p class="font-medium mb-1">前台关联</p>
        <p class="text-blue-600">匹配规则控制前台「专家推荐」算法的权重分配，影响用户看到的专家排序</p>
      </div>
    </div>

    <!-- 配置卡片 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-2 mb-3">
        <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h4 class="text-sm font-black text-slate-900">专家匹配算法权重</h4>
      </div>
      <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

      <div class="bg-blue-50 text-blue-700 rounded-lg p-3 flex items-start gap-2 text-sm mb-4">
        <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>三项权重之和应等于 1.0（100%）</span>
      </div>

      <el-form :model="config" label-width="90px" size="small">
        <el-form-item label="话题匹配">
          <el-slider v-model="config.topicMatch" :min="0" :max="1" :step="0.05" show-input />
        </el-form-item>
        <el-form-item label="风格匹配">
          <el-slider v-model="config.styleMatch" :min="0" :max="1" :step="0.05" show-input />
        </el-form-item>
        <el-form-item label="阶段匹配">
          <el-slider v-model="config.stageMatch" :min="0" :max="1" :step="0.05" show-input />
        </el-form-item>
        <el-form-item>
          <div
            :class="[
              'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-lg',
              matchingSum === 1
                ? 'bg-emerald-50 text-emerald-600'
                : 'bg-red-50 text-red-600'
            ]"
          >
            <svg v-if="matchingSum === 1" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <svg v-else class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            总和: {{ matchingSum.toFixed(2) }}
            <span v-if="matchingSum !== 1">（必须等于 1.0）</span>
          </div>
        </el-form-item>
      </el-form>
    </div>

    <!-- 底部操作确认 -->
    <div v-if="hasPerm(PERMISSIONS.EDIT_CONFIG)" class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-2 mb-3">
        <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h4 class="text-sm font-black text-slate-900">操作确认</h4>
      </div>
      <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

      <div class="bg-amber-50 text-amber-700 rounded-lg p-3 flex items-start gap-2 text-sm mb-4">
        <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>修改匹配规则将记录审计日志，请填写操作理由</span>
      </div>

      <el-form size="small">
        <el-form-item label="修改理由" required>
          <el-input
            v-model="auditReason"
            type="textarea"
            :rows="2"
            placeholder="请说明修改匹配规则的理由，将记录到审计日志"
          />
        </el-form-item>
        <el-form-item>
          <div class="flex items-center gap-3">
            <button
              @click="handleReset"
              class="px-4 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors"
            >
              恢复默认值
            </button>
            <button
              @click="handleSave"
              class="px-5 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
            >
              保存变更
            </button>
          </div>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { MATCHING_CONFIG, PERMISSIONS } from '../../data/adminConfig'
import { logAction } from '../../services/audit'
import { mapGetters } from 'vuex'

const DEFAULT_CONFIG = { ...MATCHING_CONFIG.weights }

export default {
  name: 'MatchingRules',

  data() {
    return {
      config: { ...DEFAULT_CONFIG },
      auditReason: '',
    }
  },

  computed: {
    ...mapGetters(['hasPermission']),

    PERMISSIONS() {
      return PERMISSIONS
    },

    matchingSum() {
      return this.config.topicMatch + this.config.styleMatch + this.config.stageMatch
    },
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    handleSave() {
      if (!this.hasPerm(PERMISSIONS.EDIT_CONFIG)) {
        this.$message.error('您没有编辑配置的权限')
        return
      }

      if (!this.auditReason.trim()) {
        this.$message.error('请填写修改理由')
        return
      }

      if (this.matchingSum !== 1) {
        this.$message.error('匹配权重之和必须等于 1.0')
        return
      }

      logAction({
        action: 'CONFIG_CHANGE',
        targetType: 'matching-rules',
        targetId: 'matching-config',
        reason: this.auditReason,
        source: 'admin-ui',
        actor: { role: this.$store.state.userRole },
      })

      this.$message.success('匹配规则已保存')
      this.auditReason = ''
    },

    handleReset() {
      this.$confirm('确定恢复匹配规则为默认值？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        this.config = { ...DEFAULT_CONFIG }
        this.$message.success('已恢复默认值')
      }).catch(() => {})
    },
  },
}
</script>
